/**
 * Which printing a card is, said on the card itself.
 *
 * The owner's rule, and it is a pricing rule as much as a design one:
 * "throughout the app we need that little marker of the variant. Image has 1st
 * ed base set, all should say 1st ed... That's everywhere even in the scan and
 * find, if it sees that stamp etc it should know."
 *
 * It matters because every printing shares one piece of artwork, so the picture
 * cannot tell a 1st Edition Charizard from an Unlimited one - and the gap
 * between them is the gap between forty dollars and four hundred. A card gets
 * screenshotted, shared, scrolled past and compared against another screen, so
 * the marker has to ride the card rather than sit in a header above it.
 *
 * Colour comes from the same table the slot grids use, so one printing is one
 * colour wherever it appears: a badge learned in a master set is the same badge
 * on a scan result and on a card in somebody's collection.
 */
import {
  printingLabel, printingShortLabel, printingColor, printingTextColor, isPlainFinish,
} from './wish/printings';

/**
 * All four axes, because this recomputes the label rather than being handed
 * one, and a badge that only knows two of them says "Standard" on a reverse
 * holo and on a Master Ball. The grid was building the right label and this
 * was quietly throwing it away.
 *
 * @param {object} props
 * @param {string|null} props.subtype  `cards.v_subtype`, e.g. "shadowless"
 * @param {string[]|null} props.stamps `cards.v_stamps`, e.g. ["1st-edition"]
 * @param {string|null} props.foil     `cards.v_foil`, e.g. "masterball"
 * @param {string|null} props.type     `cards.v_type`, e.g. "reverse"
 * @param {boolean} props.short        the abbreviated label, for a small tile
 * @param {object} props.style         placement; the caller owns where it sits
 */
export default function PrintingBadge({
  subtype = null, stamps = null, foil = null, type = null, short = false, style = null,
}) {
  const list = Array.isArray(stamps) ? stamps.filter(Boolean) : [];
  const label = short
    ? printingShortLabel(subtype, list, foil, type)
    : printingLabel(subtype, list, foil, type);
  // No printing recorded is not the same as a plain printing, and guessing
  // "Unlimited" from an empty column would be inventing provenance. Nothing is
  // drawn, which reads as "not stated" rather than as a claim.
  if (!label) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: '6px',
        backgroundColor: printingColor(subtype, list, foil, type),
        color: printingTextColor(subtype, list, foil, type),
        fontSize: '9px',
        fontWeight: '800',
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...(style || {}),
      }}
    >
      {label}
    </span>
  );
}

/** Whether a card row carries a printing worth showing at all. */
export function hasPrinting(card) {
  if (!card) return false;
  const stamps = Array.isArray(card.v_stamps) ? card.v_stamps.filter(Boolean) : [];
  // A reverse holo is worth saying even when nothing else about the printing
  // was recorded: it is the difference between two cards that look identical.
  return Boolean(card.v_subtype) || stamps.length > 0
    || Boolean(card.v_foil) || !isPlainFinish(card.v_type);
}

/** The badge for an owned `public.cards` row, which stores its printing in
 *  `v_subtype`, `v_stamps`, `v_foil` and `v_type`. */
export function OwnedPrintingBadge({ card, short = true, style = null }) {
  if (!hasPrinting(card)) return null;
  return (
    <PrintingBadge
      subtype={card.v_subtype}
      stamps={card.v_stamps}
      foil={card.v_foil}
      type={card.v_type}
      short={short}
      style={style}
    />
  );
}
